import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from '../../environments/environment';
import {
  PatientAppointment,
  ActivePatientFieldsForAiAnalysis,
  PatientEssentialInfo,
} from '../models/patient-model';

interface GetPatientIdResponse {
  patient_id: string;
}

@Injectable({
  providedIn: 'root',
})
export class PatientService {
  private apiUrl = environment.apiUrl;

  constructor(private http: HttpClient) {}

  addHeader(): HttpHeaders {
    const token = sessionStorage.getItem('psicogeniusAccessToken');

    let headers = new HttpHeaders();
    if (token) {
      headers = headers.set('Authorization', `Bearer ${token}`);
    }
    return headers;
  }

  getPatient(patientId: string): Observable<any> {
    const endpoint = this.apiUrl + '/patient/' + patientId;
    const headers = this.addHeader();
    return this.http.get(endpoint, { headers });
  }

  getPatientEssentialInfo(patientId: string): Observable<PatientEssentialInfo> {
    const endpoint = this.apiUrl + '/patient_essential_info/' + patientId;
    const headers = this.addHeader();
    return this.http.get<PatientEssentialInfo>(endpoint, { headers });
  }

  getPatientFieldsForAiAnalysis(
    patientId: string
  ): Observable<ActivePatientFieldsForAiAnalysis> {
    const endpoint =
      this.apiUrl + '/patient_fields_for_ai_analysis/' + patientId;
    const headers = this.addHeader();
    return this.http.get<ActivePatientFieldsForAiAnalysis>(endpoint, {
      headers,
    });
  }

  getPatients(): Observable<any> {
    const endpoint = this.apiUrl + '/patients';
    const headers = this.addHeader();
    return this.http.get(endpoint, { headers });
  }

  getPatientListWithNextInterview(): Observable<any> {
    const endpoint = this.apiUrl + '/patients_and_next_appointments';
    const headers = this.addHeader();
    return this.http.get(endpoint, { headers });
  }

  createNewPatient(data: { [key: string]: any }): Observable<any> {
    const endpoint = this.apiUrl + '/patients/create';
    const headers = this.addHeader();
    return this.http.post(endpoint, data, { headers: headers });
  }

  putPatient(patientId: string, data: any): Observable<any> {
    const endpoint = this.apiUrl + '/patients/' + patientId;
    const headers = this.addHeader();
    return this.http.put(endpoint, data, { headers: headers });
  }

  deletePatient(patientId: string): Observable<any> {
    const endpoint = this.apiUrl + '/patients/' + patientId;
    const headers = this.addHeader();
    return this.http.delete(endpoint, { headers: headers });
  }

  getNotes(patientId: string): Observable<any> {
    const endpoint = this.apiUrl + '/patient_notes/' + patientId;
    const headers = this.addHeader();
    return this.http.get(endpoint, { headers });
  }

  createNote(data: any): Observable<any> {
    const endpoint = this.apiUrl + '/patient_notes/create';
    const headers = this.addHeader();
    return this.http.post(endpoint, data, { headers: headers });
  }

  updateNote(noteId: string, data: any): Observable<any> {
    const endpoint = this.apiUrl + '/patient_notes/' + noteId;
    const headers = this.addHeader();
    return this.http.put(endpoint, data, { headers: headers });
  }

  deleteNote(noteId: string): Observable<any> {
    const endpoint = this.apiUrl + '/patient_notes/' + noteId;
    const headers = this.addHeader();
    return this.http.delete(endpoint, { headers: headers });
  }

  getReminders(patientId: string): Observable<any> {
    const endpoint = this.apiUrl + '/patient_reminders/' + patientId;
    const headers = this.addHeader();
    return this.http.get(endpoint, { headers });
  }

  createReminder(data: any): Observable<any> {
    const endpoint = this.apiUrl + '/patient_reminders/create';
    const headers = this.addHeader();
    return this.http.post(endpoint, data, { headers: headers });
  }

  updateReminder(reminderId: string, data: any): Observable<any> {
    const endpoint = this.apiUrl + '/patient_reminders/' + reminderId;
    const headers = this.addHeader();
    return this.http.put(endpoint, data, { headers: headers });
  }

  deleteReminder(reminderId: string): Observable<any> {
    const endpoint = this.apiUrl + '/patient_reminders/' + reminderId;
    const headers = this.addHeader();
    return this.http.delete(endpoint, { headers: headers });
  }

  getAppointments(patientId: string): Observable<any> {
    const endpoint = this.apiUrl + '/appointments/' + patientId;
    const headers = this.addHeader();
    return this.http.get(endpoint, { headers });
  }

  getAppointment(appointmentId: string): Observable<any> {
    const endpoint = this.apiUrl + '/appointment/' + appointmentId;
    const headers = this.addHeader();
    return this.http.get(endpoint, { headers });
  }

  createAppointment(data: any): Observable<any> {
    const endpoint = this.apiUrl + '/appointments/create';
    const headers = this.addHeader();
    return this.http.post(endpoint, data, { headers: headers });
  }
  putAppointment(appointmentId: string, data: any): Observable<any> {
    const endpoint = this.apiUrl + '/appointments/' + appointmentId;
    const headers = this.addHeader();
    return this.http.put(endpoint, data, { headers: headers });
  }
  deleteAppointment(appointmentId: string): Observable<any> {
    const endpoint = this.apiUrl + '/appointments/' + appointmentId;
    const headers = this.addHeader();
    return this.http.delete(endpoint, { headers: headers });
  }

  getAnamnesis(patientId: string): Observable<any> {
    const endpoint = this.apiUrl + '/anamnesis/' + patientId;
    const headers = this.addHeader();
    return this.http.get(endpoint, { headers });
  }

  putAnamnesis(anamnesisId: string, data: any): Observable<any> {
    const endpoint = this.apiUrl + '/anamnesis/' + anamnesisId;
    const headers = this.addHeader();
    return this.http.put(endpoint, data, { headers: headers });
  }

  getFamilyMembers(patientId: string): Observable<any> {
    const endpoint = this.apiUrl + '/patient_family_member/' + patientId;
    const headers = this.addHeader();
    return this.http.get(endpoint, { headers });
  }
  createFamilyMember(data: any): Observable<any> {
    const endpoint = this.apiUrl + '/patient_family_member/create';
    const headers = this.addHeader();
    return this.http.post(endpoint, data, { headers: headers });
  }

  deleteFamilyMember(familyMemberId: string): Observable<any> {
    const endpoint = this.apiUrl + '/patient_family_member/' + familyMemberId;
    const headers = this.addHeader();
    return this.http.delete(endpoint, { headers: headers });
  }

  putFamilyMember(familyMemberId: string, data: any): Observable<any> {
    const endpoint = this.apiUrl + '/patient_family_member/' + familyMemberId;
    const headers = this.addHeader();
    return this.http.put(endpoint, data, { headers: headers });
  }

  getDiagnoses(patientId: string): Observable<any> {
    const endpoint = this.apiUrl + '/diagnoses/' + patientId;
    const headers = this.addHeader();
    return this.http.get(endpoint, { headers });
  }
  createDiagnoses(data: any): Observable<any> {
    const endpoint = this.apiUrl + '/diagnoses/create';
    const headers = this.addHeader();
    return this.http.post(endpoint, data, { headers: headers });
  }

  deleteDiagnosis(diagnosisId: string): Observable<any> {
    const endpoint = this.apiUrl + '/diagnoses/' + diagnosisId;
    const headers = this.addHeader();
    return this.http.delete(endpoint, { headers: headers });
  }

  putDiagnosis(diagnosisId: string, data: any): Observable<any> {
    const endpoint = this.apiUrl + '/diagnoses/' + diagnosisId;
    const headers = this.addHeader();
    return this.http.put(endpoint, data, { headers: headers });
  }
  getLifeLineEventsWithSubEvents(patientId: string): Observable<any> {
    const endpoint =
      this.apiUrl + '/life_line_events_with_sub_events/' + patientId;
    const headers = this.addHeader();
    return this.http.get(endpoint, { headers });
  }
  createLifeLineEvent(data: any): Observable<any> {
    const endpoint = this.apiUrl + '/life_line_events/create';
    const headers = this.addHeader();
    return this.http.post(endpoint, data, { headers: headers });
  }
  putLifeLineEvent(lifeLineId: string, data: any): Observable<any> {
    const endpoint = this.apiUrl + '/life_line_events/' + lifeLineId;
    const headers = this.addHeader();
    return this.http.put(endpoint, data, { headers: headers });
  }
  deleteLifeLineEvent(lifeLineId: string): Observable<any> {
    const endpoint = this.apiUrl + '/life_line_events/' + lifeLineId;
    const headers = this.addHeader();
    return this.http.delete(endpoint, { headers: headers });
  }
  createLifeLineSubEvent(data: any): Observable<any> {
    const endpoint = this.apiUrl + '/life_line_sub_events/create';
    const headers = this.addHeader();
    return this.http.post(endpoint, data, { headers: headers });
  }
  deleteLifeLineSubEvent(subEventId: string): Observable<any> {
    const endpoint = this.apiUrl + '/life_line_sub_events/' + subEventId;
    const headers = this.addHeader();
    return this.http.delete(endpoint, { headers: headers });
  }
  putLifeLineSubEvent(subEventId: string, data: any): Observable<any> {
    const endpoint = this.apiUrl + '/life_line_sub_events/' + subEventId;
    const headers = this.addHeader();
    return this.http.put(endpoint, data, { headers: headers });
  }

  getDocuments(patientId: string): Observable<any> {
    const endpoint = this.apiUrl + '/patient_documents/' + patientId;
    const headers = this.addHeader();
    return this.http.get(endpoint, { headers });
  }

  createNewDocument(data: string, file: File): Observable<any> {
    const endpoint = this.apiUrl + '/patient_documents/upload_and_create';
    const formData = new FormData();
    formData.append('file', file);
    formData.append('data', data);
    const headers = this.addHeader();
    return this.http.post(endpoint, formData, { headers: headers });
  }

  getDocumentFile(documentId: string): Observable<Blob> {
    const endpoint = this.apiUrl + '/patient_documents/download/' + documentId;
    const headers = this.addHeader();

    return this.http.get(endpoint, { headers: headers, responseType: 'blob' });
  }

  deleteDocument(documentId: string): Observable<any> {
    const endpoint = this.apiUrl + '/patient_documents/' + documentId;
    const headers = this.addHeader();
    return this.http.delete(endpoint, { headers: headers });
  }

  putDocument(documentId: string, data: any): Observable<any> {
    const endpoint = this.apiUrl + '/patient_documents/' + documentId;
    const headers = this.addHeader();
    return this.http.put(endpoint, data, { headers: headers });
  }

  getPastAppointments(patientId: string): Observable<PatientAppointment[]> {
    const endpoint = `${this.apiUrl}/appointments/${patientId}/past`;
    const headers = this.addHeader();
    return this.http.get<PatientAppointment[]>(endpoint, { headers });
  }
}
